'use client';

import {
    Box,
    Divider,
    Drawer,
    IconButton,
    MenuList,
    MenuItem as MuiMenuItem,
    Stack,
    Slide,
    alpha,
    Link as MuiLink, Typography
} from "@mui/material";
import NextLink from 'next/link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Logo from "@/app/features/shared/layout/logo";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import clsx from "clsx";
import {
    MOBILE_MENU_ITEMS,
    MobileMenuItem,
    SubmenuItem as HeaderMenuItem
} from "@/app/features/sites/header/types/menu-items";
import { usePathname, useRouter } from 'next/navigation';
import CookieButton from "@/app/__to_be_integrated/_____components/floating-edge-menu/components/cookies/CookieButton";
import A11yButton from "@/app/__to_be_integrated/_____components/floating-edge-menu/components/a11y/A11yButton";
import FlyoutView from "@/app/features/sites/header/ui/desktop/flyout-info/FlyoutView";
import { ChevronRight } from "@mui/icons-material";

export default function MobileMenuDrawer() {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [activeItem, setActiveItem] = useState<HeaderMenuItem | null>(null);
    const [submenuOpen, setSubmenuOpen] = useState(false);
    const router = useRouter();
    const pathname = usePathname();

    // Close the drawer automatically on route changes (e.g., when a link inside submenu is clicked)
    useEffect(() => {
        if (isDrawerOpen) {
            setIsDrawerOpen(false);
            setSubmenuOpen(false);
            setActiveItem(null);
        }
    }, [pathname]);

    useEffect(() => {
        if (!isDrawerOpen) {
            return;
        }

        const html = document.documentElement;
        const body = document.body;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const previousHtmlOverflow = html.style.overflow;
        const previousHtmlOverscrollBehavior = html.style.overscrollBehavior;
        const previousBodyOverflow = body.style.overflow;
        const previousBodyOverscrollBehavior = body.style.overscrollBehavior;
        const previousBodyPosition = body.style.position;
        const previousBodyTop = body.style.top;
        const previousBodyLeft = body.style.left;
        const previousBodyRight = body.style.right;
        const previousBodyWidth = body.style.width;

        html.style.overflow = 'hidden';
        html.style.overscrollBehavior = 'none';
        body.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';

        return () => {
            html.style.overflow = previousHtmlOverflow;
            html.style.overscrollBehavior = previousHtmlOverscrollBehavior;
            body.style.overflow = previousBodyOverflow;
            body.style.overscrollBehavior = previousBodyOverscrollBehavior;
            body.style.position = previousBodyPosition;
            body.style.top = previousBodyTop;
            body.style.left = previousBodyLeft;
            body.style.right = previousBodyRight;
            body.style.width = previousBodyWidth;
            window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
        };
    }, [isDrawerOpen]);

    const handleItemClick = (item: MobileMenuItem) => {
        if (item.hasFlyout) {
            setActiveItem(item);
            setSubmenuOpen(true);
        } else if (item.route) {
            router.push(item.route);
            setIsDrawerOpen(false);
        }
    };

    const handleBack = () => setSubmenuOpen(false);

    const handleClose = () => { setIsDrawerOpen(false); setSubmenuOpen(false); setActiveItem(null); };

    return (
        <>
            <IconButton
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); setIsDrawerOpen(true); }}
                aria-label="Menü öffnen"
            >
                <MenuIcon />
            </IconButton>

            <Drawer
                className="mobile-menu-drawer"
                open={isDrawerOpen}
                anchor="right"
                onClose={handleClose}
                ModalProps={{
                    keepMounted: false,
                    disableRestoreFocus: true,
                    disableScrollLock: true,
                }}
                sx={{
                    width: '90vw',
                    maxWidth: '90vw',
                }}
                PaperProps={{
                    sx: (theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: 'none',
                        width: '90vw',
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        overscrollBehaviorY: 'contain',
                    }),
                }}
            >
                <Stack sx={{ height: '100%', gap: 2 }}>
                    <HeaderRow activeItem={activeItem} handleBack={handleBack} handleClose={handleClose} />
                    <Box className="px-4"><Divider /></Box>

                    {/* Content area */}
                    <Box className={clsx(
                        "mobile-menu-drawer-content no-scrollbar flex flex-auto min-h-0 min-w-0 overflow-x-hidden",
                        (activeItem || submenuOpen) ? "overflow-y-hidden" : "overflow-y-auto"
                    )}
                    role="region"
                    aria-label="Menüinhalt"
                    tabIndex={0}
                    sx={{
                        overscrollBehaviorY: 'contain',
                    }}
                    >
                        {(activeItem || submenuOpen) ? (
                            // Subview content (reuse desktop slideOut component) with smooth slide-in and exit
                            <Slide in={submenuOpen} direction="left" mountOnEnter unmountOnExit timeout={300} onExited={() => setActiveItem(null)}>
                                <Box
                                    className="mobile-submenu-item no-scrollbar"
                                    sx={{
                                        display: 'flex',
                                        flex: '1 1 auto',
                                        minHeight: 0,
                                        minWidth: 0,
                                        overflowY: 'inherit',
                                        overscrollBehaviorY: 'contain',
                                    }}
                                >
                                    <FlyoutView activeMenuId={activeItem?.id ?? null} />
                                </Box>
                            </Slide>
                        ) : (
                            // Main list
                            <MenuList
                                className="flex-auto m-4"
                                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                            >
                                {MOBILE_MENU_ITEMS.map((item, index) => {
                                    return item.type == 'separator'
                                        ? (
                                            <Box key={`sep-${index}`} sx={{ paddingY: 4 }} >
                                                <Divider />
                                            </Box>
                                        ) : (
                                            <MenuItemView
                                                key={item.id}
                                                item={item}
                                                onClick={handleItemClick}
                                            />
                                        )
                                })}
                            </MenuList>
                        )}
                    </Box>
                    <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between" className="m-4">
                        <Stack direction="row" spacing={2} justifyContent="right">
                            <CookieButton />
                            <A11yButton />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <MuiLink component={NextLink} href="/impressum" prefetch>Impressum</MuiLink>
                            <MuiLink component={NextLink} href="/privacy" prefetch>Datenschutz</MuiLink>
                        </Stack>
                    </Stack>
                </Stack>
            </Drawer>
        </>
    )
}

function HeaderRow({
    activeItem,
    handleBack,
    handleClose
}: {
    activeItem: HeaderMenuItem | null,
    handleBack: () => void,
    handleClose: () => void
}) {
    return (
        <Stack
            className="p-4"
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
        >
            {/* Left snap-section */}
            {activeItem ? (
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton aria-label="Zurück" onClick={handleBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">{activeItem.label}</Typography>
                </Stack>
            ) : (
                <Logo variant="text" />
            )}
            <IconButton aria-label="Menü schließen" onClick={handleClose} sx={{ alignSelf: 'start' }}>
                <CloseIcon />
            </IconButton>
        </Stack>
    )
}

function MenuItemView({ item, onClick }: { item: MobileMenuItem, onClick: (item: MobileMenuItem) => void }) {
    return (
        <MuiMenuItem
            key={item.id} onClick={() => onClick(item)}
            sx={{
                boxShadow: (theme) => theme.shadows[1],
                borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, .125)}`,
                borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, .125)}`,
                alignItems: 'stretch',
                minHeight: 'auto',
                py: 1,
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', gap: 1 }}>
                <Stack direction="row" alignItems="flex-start" gap={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {item.label}
                    </Typography>
                </Stack>
                {item.hasFlyout && <ChevronRight sx={{ opacity: 0.6, alignSelf: 'center', flexShrink: 0 }} aria-hidden />}
            </Stack>
        </MuiMenuItem>
    )
}
